import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { PublicSiteView } from '@borradh-workspace/contracts';
import { Globe, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useSiteDomainUpdate } from './use-site-domain-update';

const statusLabels = {
  pending_dns: 'Waiting for DNS',
  verifying: 'Verifying HTTPS',
  active: 'Connected',
  error: 'Needs attention',
  disconnecting: 'Disconnecting',
};
export function SiteDomainControls({
  locationId,
  site,
  disabled,
  onUpdated,
}: {
  locationId: string;
  site: PublicSiteView;
  disabled: boolean;
  onUpdated: (site: PublicSiteView) => void;
}) {
  const [hostname, setHostname] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const mutation = useSiteDomainUpdate(locationId, {
    onSuccess: onUpdated,
    onError: (error: Error) => toast.error(error.message),
  });
  const domain = site.domain;
  const busy = disabled || mutation.isPending;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Globe className="size-4" />
          Domain
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Your domain</DialogTitle>
          <DialogDescription>
            Use your own web address for this website.
          </DialogDescription>
        </DialogHeader>
        {domain ? (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <p className="break-all font-medium">{domain.hostname}</p>
              <span className="shrink-0 text-sm text-muted-foreground">
                {statusLabels[domain.status]}
              </span>
            </div>
            {domain.records.length > 0 && domain.status !== 'active' && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Add these records at your DNS provider. For Cloudflare, use
                  DNS only.
                </p>
                <div className="overflow-x-auto rounded-xl border">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b bg-muted/40">
                      <tr>
                        <th className="p-3">Type</th>
                        <th className="p-3">Name</th>
                        <th className="p-3">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {domain.records.map((record) => (
                        <tr
                          key={`${record.type}:${record.name}:${record.value}`}
                          className="border-b last:border-0"
                        >
                          <td className="p-3 align-top">{record.type}</td>
                          <td className="break-all p-3 align-top select-all">
                            {record.name}
                          </td>
                          <td className="break-all p-3 align-top select-all">
                            {record.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {domain.error && (
              <p role="alert" className="text-sm text-destructive">
                {domain.error}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirmDisconnect(true)}
              >
                Disconnect
              </Button>
              <Button
                disabled={busy}
                onClick={() =>
                  domain.status === 'disconnecting'
                    ? mutation.disconnect()
                    : mutation.check()
                }
              >
                {mutation.isPending && (
                  <Loader2 className="size-4 animate-spin" />
                )}
                {domain.status === 'disconnecting'
                  ? 'Retry disconnect'
                  : 'Check connection'}
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.connect(hostname.trim());
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="website-domain">Domain name</Label>
              <Input
                id="website-domain"
                placeholder="www.yourbusiness.com"
                autoComplete="off"
                value={hostname}
                onChange={(event) => setHostname(event.target.value)}
                disabled={busy}
              />
            </div>
            {!site.publishedVersionId && (
              <p className="text-sm text-muted-foreground">
                Publish your website to connect a domain.
              </p>
            )}
            <Button
              className="w-full"
              type="submit"
              disabled={busy || !hostname.trim() || !site.publishedVersionId}
            >
              {mutation.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Connect domain
            </Button>
          </form>
        )}
        <ConfirmDeleteDialog
          open={confirmDisconnect}
          onOpenChange={setConfirmDisconnect}
          title={`Disconnect ${domain?.hostname}?`}
          description="Your website stays available at its default address. This domain will stop serving it. Your DNS records will stay at your provider."
          confirmLabel="Disconnect domain"
          cancelLabel="Keep domain"
          icon={Globe}
          isPending={mutation.isPending}
          confirmDisabled={busy}
          onConfirm={mutation.disconnect}
        />
      </DialogContent>
    </Dialog>
  );
}
